// completionVariance.js — Modal content for Completion Variance KPI
// Place at: src/components/modalContent/productionMilestonesModalComponents/completionVariance.js

import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";
import {
  COMPLETION_VARIANCE_TREND,
  VARIANCE_BY_MODULE,
} from "../../../mockData/mockProductionMilestonesData";

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  const v = payload[0].value;
  return (
    <div className="pml-chart-tooltip">
      <div className="pml-chart-tooltip-date">{label}</div>
      <div className="pml-chart-tooltip-row">
        <div
          className="pml-chart-tooltip-dot"
          style={{ background: v >= 0 ? "#38a169" : "#cc0000" }}
        />
        {v >= 0 ? `+${v}` : v}% variance
      </div>
    </div>
  );
};

const CompletionVariance = () => {
  const avgVariance = (
    COMPLETION_VARIANCE_TREND.reduce((s, d) => s + d.variance, 0) /
    COMPLETION_VARIANCE_TREND.length
  ).toFixed(1);

  const positiveWeeks = COMPLETION_VARIANCE_TREND.filter(
    (d) => d.variance >= 0,
  ).length;
  const negativeWeeks = COMPLETION_VARIANCE_TREND.length - positiveWeeks;

  return (
    <div className="pml-modal-flex-content">
      {/* Stat band */}
      <div className="pml-modal-stat-band pml-modal-stat-band--3">
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">8-Week Avg Variance</span>
          <span
            className={`pml-modal-stat-value ${parseFloat(avgVariance) >= 0 ? "pml-modal-stat-value--ok" : "pml-modal-stat-value--alert"}`}
          >
            {avgVariance >= 0 ? `+${avgVariance}` : avgVariance}%
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Weeks Ahead</span>
          <span className="pml-modal-stat-value pml-modal-stat-value--ok">
            {positiveWeeks}
          </span>
        </div>
        <div className="pml-modal-stat">
          <span className="pml-modal-stat-label">Weeks Behind</span>
          <span
            className={`pml-modal-stat-value ${negativeWeeks > 0 ? "pml-modal-stat-value--alert" : "pml-modal-stat-value--ok"}`}
          >
            {negativeWeeks}
          </span>
        </div>
      </div>

      {/* Bar chart */}
      <p className="pml-modal-sub-title">8-Week Variance Trend</p>
      <div style={{ height: 200 }}>
        <ResponsiveContainer width="100%" height="100%">
          <BarChart
            data={COMPLETION_VARIANCE_TREND}
            margin={{ top: 4, right: 8, left: 0, bottom: 4 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#e2e8f0"
              vertical={false}
            />
            <XAxis
              dataKey="week"
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={{ stroke: "#e2e8f0" }}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#718096" }}
              tickLine={false}
              axisLine={false}
              tickFormatter={(v) => `${v > 0 ? "+" : ""}${v}%`}
              width={42}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine y={0} stroke="#cbd5e0" strokeWidth={1.5} />
            <Bar dataKey="variance" radius={[4, 4, 0, 0]}>
              {COMPLETION_VARIANCE_TREND.map((entry, index) => (
                <Cell
                  key={index}
                  fill={entry.variance >= 0 ? "#38a169" : "#cc0000"}
                  fillOpacity={0.85}
                />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Per-module variance table */}
      <p className="pml-modal-sub-title">Module-Level Variance</p>
      <div className="pml-modal-scroll-wrap pml-table-auto-height">
        <table className="pml-modal-table">
          <thead>
            <tr>
              <th>Module</th>
              <th>Scheduled Completion</th>
              <th>Actual Completion</th>
              <th>Variance (days)</th>
            </tr>
          </thead>
          <tbody>
            {VARIANCE_BY_MODULE.map((row, i) => {
              const cls =
                row.varianceDays === 0
                  ? "pml-variance-cell--zero"
                  : row.varianceDays < 0
                    ? "pml-variance-cell--pos"
                    : "pml-variance-cell--neg";
              return (
                <tr key={i}>
                  <td>{row.module}</td>
                  <td>{row.scheduledDay}</td>
                  <td>{row.actualDay}</td>
                  <td className={cls}>
                    {row.varianceDays === 0
                      ? "On time"
                      : row.varianceDays < 0
                        ? `${Math.abs(row.varianceDays)}d early`
                        : `${row.varianceDays}d late`}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default CompletionVariance;
